import { cleanObject } from '@shared/utils';
import getPieveCompanyIdForRequest from '@/lib/auth/getPieveCompanyIdForRequest.js';
import { del, get, post, put } from '@/redux/requests/api.js';
import { CONVERSATION_THREAD_TYPES } from '@/lib/threads/threadTypeValues.js';
import createRequestFailure from '@/redux/requests/lib/createRequestFailure.js';
import normalizeRequestFailure from '@/redux/requests/lib/normalizeRequestFailure.js';

const normalizeOptionalCsv = (value) => {
  if (value === undefined || value === null) {
    return undefined;
  }
  if (Array.isArray(value)) {
    const items = value.map((item) => String(item || '').trim()).filter(Boolean);
    return items.length > 0 ? items.join(',') : undefined;
  }
  return String(value || '').trim() || undefined;
};

const getThreads = ({ limit, cursor, archived, threadTypeIn, threadTypeNotIn, projectSections, perProjectLimit, projectId, companyId, search, signal } = {}, callback) => {
  return get('/v1/pieve/threads', {
    signal,
    callback,
    companyId,
    query: cleanObject({ limit, cursor, archived, threadTypeIn: normalizeOptionalCsv(threadTypeIn), threadTypeNotIn: normalizeOptionalCsv(threadTypeNotIn), projectSections, perProjectLimit, projectId, search }, { strict: true }),
    onResponse: (responseShape) => normalizeRequestFailure({ responseShape, fallbackMessage: 'Failed to load tasks' })
  });
};

/* The sidebar search box. The sidebar's thread list is paginated, so a name match has to run on
   the server; the hits land in their own reducer slot and never touch the sidebar list itself. */
const SIDEBAR_SEARCH_THREAD_LIMIT = 20;

const searchSidebarThreads = ({ search, signal } = {}, callback) => {
  return get('/v1/pieve/threads', {
    action: 'SEARCH_SIDEBAR_THREADS',
    signal,
    callback,
    query: cleanObject({ search, threadTypeIn: CONVERSATION_THREAD_TYPES.join(','), archived: 'exclude', limit: SIDEBAR_SEARCH_THREAD_LIMIT }, { strict: true }),
    onResponse: (responseShape) => normalizeRequestFailure({ responseShape, fallbackMessage: 'Failed to search tasks' })
  });
};

const getArchivedThreads = ({ signal } = {}, callback) => {
  return get('/v1/pieve/archived-threads', {
    action: 'GET_ARCHIVED_THREADS',
    signal,
    callback,
    onResponse: (responseShape) => normalizeRequestFailure({ responseShape, fallbackMessage: 'Failed to load deleted tasks' })
  });
};

const createThread = ({ title, parentThreadId, projectId, sandboxMode, debugMode, threadType, composerModel, composerReasoningEffort, pinned, sidebarPlacement, signal } = {}, callback) => {
  return post('/v1/pieve/threads', {
    signal,
    callback,
    data: cleanObject({ title, parentThreadId, projectId, sandboxMode, debugMode, threadType, composerModel, composerReasoningEffort, pinned, sidebarPlacement }),
    onResponse: (responseShape) => normalizeRequestFailure({ responseShape, fallbackMessage: 'Failed to create task' })
  });
};

const duplicateThread = ({ threadId, signal } = {}, callback) => {
  const id = String(threadId || '').trim();
  if (!id) {
    return Promise.resolve(createRequestFailure({ message: 'threadId is required' }));
  }

  return post(`/v1/pieve/threads/${encodeURIComponent(id)}/duplicate`, {
    action: 'DUPLICATE_THREAD',
    signal,
    callback
  });
};

const getThread = ({ threadId, signal } = {}, callback) => {
  const id = String(threadId || '').trim();
  if (!id) {
    return Promise.resolve(createRequestFailure({ message: 'threadId is required' }));
  }

  return get(`/v1/pieve/threads/${encodeURIComponent(id)}`, {
    signal,
    callback,
    onResponse: (responseShape) => normalizeRequestFailure({ responseShape, fallbackMessage: 'Task not found' })
  });
};

const getThreadRead = ({ threadId, includeTurns, signal } = {}, callback) => {
  const id = String(threadId || '').trim();
  if (!id) {
    return Promise.resolve(createRequestFailure({ message: 'threadId is required' }));
  }

  return get(`/v1/pieve/threads/${encodeURIComponent(id)}/read`, {
    signal,
    callback,
    query: cleanObject({ includeTurns }, { strict: true }),
    onResponse: (responseShape) => normalizeRequestFailure({ responseShape, fallbackMessage: 'Failed to load task read' })
  });
};

const putThreadAppConnection = ({ threadId, appId, credentialId, signal } = {}, callback) => {
  const id = String(threadId || '').trim();
  const normalizedAppId = String(appId || '').trim();

  if (!id || !normalizedAppId || !credentialId) {
    return Promise.resolve(createRequestFailure({ message: 'threadId, appId, and credentialId are required' }));
  }

  return put(`/v1/pieve/threads/${encodeURIComponent(id)}/connections/${encodeURIComponent(normalizedAppId)}`, {
    action: 'SET_THREAD_APP_CONNECTION',
    signal,
    callback,
    meta: {
      threadId: id,
      appId: normalizedAppId
    },
    data: {
      credentialId
    },
    onResponse: (responseShape) => normalizeRequestFailure({ responseShape, fallbackMessage: 'Failed to select connection for this task' })
  });
};

const deleteThreadAppConnection = ({ threadId, appId, signal } = {}, callback) => {
  const id = String(threadId || '').trim();
  const normalizedAppId = String(appId || '').trim();

  if (!id || !normalizedAppId) {
    return Promise.resolve(createRequestFailure({ message: 'threadId and appId are required' }));
  }

  return del(`/v1/pieve/threads/${encodeURIComponent(id)}/connections/${encodeURIComponent(normalizedAppId)}`, {
    action: 'CLEAR_THREAD_APP_CONNECTION',
    signal,
    callback,
    meta: {
      threadId: id,
      appId: normalizedAppId
    },
    onResponse: (responseShape) => normalizeRequestFailure({ responseShape, fallbackMessage: 'Failed to clear connection for this task' })
  });
};

const listCompanyThreads = () => {
  const companyId = String(getPieveCompanyIdForRequest() || '').trim() || null;

  return get('/v1/pieve/company-threads', {
    action: 'GET_COMPANY_THREADS',
    loader: 'getCompanyThreads',
    meta: { companyId }
  });
};

const setThreadMode = ({ threadId, targetMode, previousMode } = {}) => {
  const id = String(threadId || '').trim();
  if (!id) {
    return Promise.resolve(createRequestFailure({ message: 'threadId is required' }));
  }

  // threadId + previousMode ride in meta so SET_THREAD_MODE_OPTIMISTIC can flip the thread type
  // instantly and SET_THREAD_MODE_FAIL can restore the prior mode on rejection.
  return put(`/v1/pieve/threads/${encodeURIComponent(id)}/mode`, {
    action: 'SET_THREAD_MODE',
    loader: 'setThreadMode',
    meta: { threadId: id, previousMode },
    data: { targetMode }
  });
};

const promoteThread = ({ threadId } = {}, callback) => {
  const id = String(threadId || '').trim();
  if (!id) {
    return Promise.resolve(createRequestFailure({ message: 'threadId is required' }));
  }

  return post(`/v1/pieve/threads/${encodeURIComponent(id)}/promote`, {
    action: 'PROMOTE_THREAD',
    loader: 'promoteThread',
    meta: { threadId: id },
    callback,
    data: {}
  });
};

export { createThread, deleteThreadAppConnection, duplicateThread, getArchivedThreads, getThread, getThreadRead, getThreads, listCompanyThreads, promoteThread, putThreadAppConnection, searchSidebarThreads, setThreadMode };
